import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../../componentes/button/button";
import Textarea from "../../../componentes/input/textarea";
import AjusteComissaoService from "../../../services/resource/AjusteComissaoService";
import SapServiceRetornoEntrega from "../../../services/resource/SapServiceRetornoEntrega";
import { formatLocalDate } from "../../../utils/formatDate";

// Os três indicadores que aceitam ajuste na comissão logística. O ajuste retira a escala/dia SÓ do
// indicador escolhido (não zera o indicador e não afeta os demais).
const INDICADORES = [
    {
        tipo: "IGNORAR_RETORNO",
        titulo: "Retorno",
        icone: "fa fa-repeat",
        descricao: "O retorno desta escala deixa de entrar no % de retorno.",
    },
    {
        tipo: "IGNORAR_GREENMILE",
        titulo: "Conclusão de Rota + Green Mile",
        icone: "fa fa-map-marker",
        descricao: "Esta rota deixa de ser avaliada em conclusão e Green Mile.",
    },
    {
        tipo: "IGNORAR_TELEMETRIA",
        titulo: "Segurança Viária",
        icone: "fa fa-shield",
        descricao: "O dia do motorista deixa de entrar na média de segurança viária.",
    },
];

function motivoIndisponivel(tipo, carga, retornos) {
    if (tipo === "IGNORAR_RETORNO" && !carga.temRetornos) return "Esta escala não tem retorno importado.";
    if (tipo === "IGNORAR_GREENMILE" && !carga.rotaGreenmileTransporte) return "Esta escala não tem rota Green Mile vinculada.";
    if (tipo === "IGNORAR_TELEMETRIA" && !carga.motoristaId) return "Esta escala não tem motorista.";
    return null;
}

/**
 * Solicitação de ajuste a partir de uma escala (linha da tabela de rotas): a escala já traz filial,
 * transporte e data, então o usuário só escolhe o indicador, (para retorno) os clientes e o motivo.
 * A trava de duplicidade e a aprovação continuam no backend.
 */
export default function SolicitarAjusteRapido({ carga, ano, mes, onClose, onSuccess }) {
    const ajusteService = useMemo(() => new AjusteComissaoService(), []);
    const retornoService = useMemo(() => new SapServiceRetornoEntrega(), []);

    const [tipo, setTipo] = useState(null);
    const [motivo, setMotivo] = useState("");
    const [erros, setErros] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [retornos, setRetornos] = useState([]);
    const [carregandoRetornos, setCarregandoRetornos] = useState(false);
    const [clientesSelecionados, setClientesSelecionados] = useState([]);

    useEffect(() => {
        if (tipo !== "IGNORAR_RETORNO" || retornos.length > 0) return;
        setCarregandoRetornos(true);
        retornoService.listar({ filialId: carga.filialId, transporte: carga.transporte })
            .then(res => {
                setRetornos(res.data ?? []);
                // por padrão, todos os clientes com retorno nesta escala
                setClientesSelecionados([...new Set((res.data ?? []).map(r => r.clienteCodigo).filter(Boolean))]);
            })
            .finally(() => setCarregandoRetornos(false));
    }, [tipo]);

    // um cartão por cliente (vários itens de retorno do mesmo cliente viram um só ajuste)
    const clientes = useMemo(() => {
        const mapa = new Map();
        retornos.forEach(r => {
            if (!r.clienteCodigo) return;
            const atual = mapa.get(r.clienteCodigo) ?? {
                codigo: r.clienteCodigo,
                nome: r.clienteFantasia || r.clienteRazaoSocial || r.clientFantasia || "—",
                cubos: 0,
                motivos: new Set(),
            };
            atual.cubos += Number(r.cubos) || 0;
            if (r.motivoNome) atual.motivos.add(r.motivoNome);
            mapa.set(r.clienteCodigo, atual);
        });
        return [...mapa.values()];
    }, [retornos]);

    const alternarCliente = (codigo) => {
        setClientesSelecionados(prev =>
            prev.includes(codigo) ? prev.filter(c => c !== codigo) : [...prev, codigo]);
    };

    const solicitar = async () => {
        setErros(true);
        if (!tipo || !motivo.trim()) {
            toast.error("Escolha o indicador e informe o motivo.");
            return;
        }
        if (tipo === "IGNORAR_RETORNO" && clientesSelecionados.length === 0) {
            toast.error("Selecione ao menos um cliente com retorno.");
            return;
        }

        const base = {
            filialId: carga.filialId,
            transporte: carga.transporte,
            tipo,
            motivo: motivo.trim(),
            ano,
            mes: Number(mes),
            isDl: true,
        };
        const pedidos = tipo === "IGNORAR_RETORNO"
            ? clientesSelecionados.map(c => ({ ...base, clienteId: Number(c) }))
            : [base];

        setSalvando(true);
        let feitos = 0;
        // um por vez: se algum for recusado (ex.: já existe ajuste), o aviso do backend aparece e os demais seguem
        for (const pedido of pedidos) {
            try {
                await ajusteService.solicitar(pedido);
                feitos++;
            } catch (e) {
                console.error(e);
            }
        }
        setSalvando(false);

        if (feitos > 0) {
            toast.success(feitos === 1 ? "Ajuste solicitado com sucesso!" : `${feitos} ajustes solicitados com sucesso!`);
            if (onSuccess) onSuccess();
            if (onClose && feitos === pedidos.length) onClose();
        }
    };

    return (
        <div className="flex flex-col gap-4 p-1">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 rounded-lg border border-border bg-muted/30 p-3 text-xs">
                <Info label="Data" value={formatLocalDate(carga.dataOperacao)} />
                <Info label="Transporte" value={carga.transporte} />
                <Info label="Filial" value={carga.filialNome} />
                <Info label="Motorista" value={carga.motoristaNome ?? "—"} />
            </div>

            <div>
                <p className="mb-2 text-sm font-medium text-foreground/80">
                    Qual indicador deve ser desconsiderado nesta escala? <span className="text-destructive">*</span>
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {INDICADORES.map(ind => {
                        const bloqueio = motivoIndisponivel(ind.tipo, carga, retornos);
                        const ativo = tipo === ind.tipo;
                        return (
                            <button
                                key={ind.tipo}
                                type="button"
                                disabled={!!bloqueio}
                                onClick={() => setTipo(ind.tipo)}
                                title={bloqueio ?? ""}
                                className={`rounded-lg border p-3 text-left transition-colors duration-150
                                    ${ativo ? "border-primary bg-primary/10" : "border-border bg-card hover:bg-muted/50"}
                                    ${bloqueio ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
                                    ${erros && !tipo ? "border-destructive" : ""}`}
                            >
                                <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                    <i className={`${ind.icone} text-teal-600`} /> {ind.titulo}
                                </p>
                                <p className="mt-1 text-xs text-muted-foreground">{bloqueio ?? ind.descricao}</p>
                            </button>
                        );
                    })}
                </div>
            </div>

            {tipo === "IGNORAR_RETORNO" && (
                <div>
                    <p className="mb-2 text-sm font-medium text-foreground/80">Clientes com retorno nesta escala</p>
                    {carregandoRetornos ? (
                        <p className="text-xs text-muted-foreground">Carregando retornos...</p>
                    ) : clientes.length === 0 ? (
                        <p className="text-xs text-destructive">Nenhum retorno com cliente encontrado para este transporte.</p>
                    ) : (
                        <div className="max-h-48 overflow-y-auto rounded-lg border border-border divide-y divide-border">
                            {clientes.map(c => (
                                <label key={c.codigo} className="flex cursor-pointer items-center gap-3 p-2 text-xs hover:bg-muted/40">
                                    <input
                                        type="checkbox"
                                        checked={clientesSelecionados.includes(c.codigo)}
                                        onChange={() => alternarCliente(c.codigo)}
                                    />
                                    <span className="font-semibold text-foreground">{c.codigo}</span>
                                    <span className="flex-1 truncate">{c.nome}</span>
                                    <span className="text-muted-foreground">{[...c.motivos].join(", ")}</span>
                                    <span className="font-medium">{c.cubos.toFixed(2)} cubos</span>
                                </label>
                            ))}
                        </div>
                    )}
                    <p className="mt-1 text-[11px] text-muted-foreground">
                        Será criado um pedido de ajuste por cliente selecionado; a escala sai do % de retorno.
                    </p>
                </div>
            )}

            <Textarea
                label="Motivo do ajuste"
                required
                value={motivo}
                onChange={e => setMotivo(e.target.value)}
                error={!motivo.trim() && erros}
                touched={erros}
            />

            <p className="text-[11px] text-muted-foreground">
                O ajuste só passa a valer na comissão depois de aprovado. Ele retira esta escala apenas do indicador escolhido;
                cubagem e os demais indicadores continuam normais.
            </p>

            <div className="flex justify-end gap-3 border-t border-border pt-3">
                <Button label="Cancelar" variant="gray" iconLeft="fa fa-arrow-left" onClick={onClose} />
                <Button label="Solicitar ajuste" variant="indigo" iconLeft="fa fa-save" loading={salvando} onClick={solicitar} />
            </div>
        </div>
    );
}

function Info({ label, value }) {
    return (
        <div>
            <p className="text-muted-foreground">{label}</p>
            <p className="font-semibold text-foreground">{value ?? "—"}</p>
        </div>
    );
}
